import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../App'
import { supabase } from '../lib/supabase'

const EMPTY_PROFILE = Object.freeze({ default_spray_count: null, region: null })

export function useUserProfileSettings() {
  const { user } = useAuth()
  const [profile, setProfile] = useState(EMPTY_PROFILE)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchProfile = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError('')
    const { data, error: queryError } = await supabase
      .from('user_profiles')
      .select('default_spray_count,region')
      .eq('user_id', user.id)
      .maybeSingle()
    if (queryError) setError(queryError.message)
    else setProfile(data || EMPTY_PROFILE)
    setLoading(false)
  }, [user])

  useEffect(() => { void fetchProfile() }, [fetchProfile])

  const saveProfile = async ({ default_spray_count, region }) => {
    setSaving(true)
    setError('')
    const { data, error: saveError } = await supabase
      .from('user_profiles')
      .upsert({
        user_id: user.id,
        default_spray_count,
        region: region?.trim() || null,
      }, { onConflict: 'user_id' })
      .select('default_spray_count,region')
      .single()
    setSaving(false)
    if (saveError) {
      setError(saveError.message)
      throw saveError
    }
    setProfile(data)
    return data
  }

  return { profile, loading, saving, error, saveProfile, refresh: fetchProfile }
}

export function useFragranceSprayOverride(fragranceId) {
  const { user } = useAuth()
  const [sprayOverride, setSprayOverride] = useState(null)
  const [loading, setLoading] = useState(Boolean(fragranceId))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchOverride = useCallback(async () => {
    if (!user || !fragranceId) {
      setSprayOverride(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    const { data, error: queryError } = await supabase
      .from('fragrances')
      .select('spray_count_override')
      .eq('user_id', user.id)
      .eq('id', fragranceId)
      .maybeSingle()
    if (queryError) setError(queryError.message)
    else setSprayOverride(data?.spray_count_override ?? null)
    setLoading(false)
  }, [user, fragranceId])

  useEffect(() => { void fetchOverride() }, [fetchOverride])

  const saveSprayOverride = async (spray_count_override) => {
    setSaving(true)
    setError('')
    const { data, error: saveError } = await supabase
      .from('fragrances')
      .update({ spray_count_override })
      .eq('user_id', user.id)
      .eq('id', fragranceId)
      .select('spray_count_override')
      .single()
    setSaving(false)
    if (saveError) {
      setError(saveError.message)
      throw saveError
    }
    setSprayOverride(data?.spray_count_override ?? null)
    return data
  }

  return { sprayOverride, loading, saving, error, saveSprayOverride, refresh: fetchOverride }
}

export function useSignificantDates({ date = null } = {}) {
  const { user } = useAuth()
  const [dates, setDates] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchDates = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError('')
    let query = supabase
      .from('user_significant_dates')
      .select('id,user_id,label,date,relationship,notes')
      .eq('user_id', user.id)
      .order('date', { ascending: true })
      .order('label', { ascending: true })
    if (date) query = query.eq('date', date)
    const { data, error: queryError } = await query
    if (queryError) setError(queryError.message)
    else setDates(data || [])
    setLoading(false)
  }, [user, date])

  useEffect(() => { void fetchDates() }, [fetchDates])

  const saveDate = async (values) => {
    setSaving(true)
    setError('')
    const row = {
      user_id: user.id,
      label: values.label.trim(),
      date: values.date,
      relationship: values.relationship?.trim() || null,
      notes: values.notes?.trim() || null,
    }
    const request = values.id
      ? supabase.from('user_significant_dates').update(row).eq('user_id', user.id).eq('id', values.id)
      : supabase.from('user_significant_dates').insert(row)
    const { data, error: saveError } = await request.select('id,user_id,label,date,relationship,notes').single()
    setSaving(false)
    if (saveError) {
      setError(saveError.message)
      throw saveError
    }
    await fetchDates()
    return data
  }

  const deleteDate = async (id) => {
    setSaving(true)
    setError('')
    const { error: deleteError } = await supabase
      .from('user_significant_dates')
      .delete()
      .eq('user_id', user.id)
      .eq('id', id)
    setSaving(false)
    if (deleteError) {
      setError(deleteError.message)
      throw deleteError
    }
    setDates(current => current.filter(item => item.id !== id))
  }

  return { dates, loading, saving, error, saveDate, deleteDate, refresh: fetchDates }
}
